//@flow

import styled from "styled-components"
import * as React from "react"
import { TweenMax, Cubic, Back } from "gsap"
import { FormattedMessage } from "react-intl"
import { colorsForLevel } from "../Shared/AQILegend"
import { MetricLabel } from "../CurrentAirQualityView"
import LocationMarker from "./LocationMarker"

import type { Metric } from "../Shared/AQILegend"

export const LocationBarsContainer = styled.ul`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  list-style: none;
  padding: 0.5rem 1rem;
  margin: 0;
  align-content: space-between;
  & + & {
    margin-left: 1rem;
  }
`
export const Bar = styled.li`
  background: ${({ background }) => background};
  color: ${({ foreground }) => foreground};
  display: block;
  list-style: none;
  margin: 0 auto;
  padding: 0;
  margin-top: ${({ percent }) => (percent ? Math.max(100 - percent, 0) : 0)}%;
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  width: 58px;
  flex-basis: 58px;
`

export const Reading = styled.h4`
  margin: 1rem auto 0.25rem auto;
  font-family: "din-condensed";
  font-weight: 400;
  font-size: 30px;
  color: ${({ color }) => color};
  text-align: center;
`

export const Unit = styled.h5`
  margin: -0.5rem auto auto auto;
  font-family: "din-condensed";
  font-weight: 400;
  font-size: 20px;
  color: ${({ color }) => color};
  text-align: center;
`

class LocationBar extends React.Component<{
  value: ?number,
  locationNumber: number,
  pollutant: Metric,
  relativeMax: number,
  delay?: number
}> {
  bar: ?HTMLElement
  marker: ?HTMLElement
  reading: ?HTMLElement
  label: ?HTMLElement

  componentDidMount() {
    const { delay } = this.props
    const baseDelay = delay || 0
    TweenMax.set(this.marker, { opacity: 0, y: "-=20" })
    TweenMax.to(this.marker, 0.5, {
      opacity: 1,
      y: 0,
      ease: Cubic.easeOut,
      delay: baseDelay + 0.25
    })

    TweenMax.set(this.reading, { opacity: 0, y: "+=10" })
    TweenMax.to(this.reading, 0.25, {
      opacity: 1,
      y: 0,
      ease: Cubic.easeOut,
      delay: baseDelay + 0.9
    })

    if (this.label) {
      TweenMax.set(this.label, { opacity: 0, y: "+=10" })
      TweenMax.to(this.label, 0.25, {
        opacity: 1,
        y: 0,
        ease: Cubic.easeOut,
        delay: baseDelay + 1.05
      })
    }

    TweenMax.set(this.bar, { opacity: 0, x: "+=10", scaleY: 0 })
    TweenMax.to(this.bar, 0.5, {
      opacity: 1,
      x: 0,
      ease: Cubic.easeOut,
      delay: baseDelay
    })
    TweenMax.to(this.bar, 1, {
      scaleY: 1,
      transformOrigin: "0 100%",
      ease: Back.easeOut,
      delay: baseDelay + 0
    })
  }

  render() {
    const { value, pollutant, relativeMax, locationNumber } = this.props
    const { foreground, background } = colorsForLevel(value)
    const percent = value && value > 0 ? value / relativeMax * 30 + 70 : 95
    return (
      <Bar
        background={background}
        foreground={foreground}
        percent={percent}
        innerRef={el => (this.bar = el)}
      >
        <Reading color={foreground} innerRef={el => (this.reading = el)}>
          {value && value > 0 ? (
            Math.round(value)
          ) : (
            <FormattedMessage id="kiosk.forecast.level.nodata" />
          )}
        </Reading>
        {value && value > 0 ? (
          <MetricLabel
            metric={pollutant}
            Wrapper={Unit}
            innerRef={el => (this.label = el)}
          />
        ) : (
          <span />
        )}
        <div
          style={{
            position: "absolute",
            bottom: "4px",
            left: "50%",
            marginLeft: "-14px"
          }}
        >
          <LocationMarker
            labelText={`${locationNumber}`}
            labelSize="24px"
            color={background}
            fill={foreground}
            height={47}
            width={27}
            innerRef={el => (this.marker = el)}
          />
        </div>
      </Bar>
    )
  }
}

export { LocationBar }

export type LocationReading = {
  location: number,
  reading: ?number,
  pollutant: Metric
}

const LocationBars = ({
  readings,
  delay
}: {
  readings: Array<LocationReading>,
  delay?: number
}) => (
  <LocationBarsContainer>
    {readings.map(({ pollutant, location, reading }, index) => (
      <LocationBar
        key={`${pollutant}_${location}_locationBar_${index}`}
        value={reading}
        pollutant={pollutant}
        relativeMax={Math.max(...readings.map(r => r.reading || 0))}
        locationNumber={location}
        delay={delay + index * 0.1}
      />
    ))}
  </LocationBarsContainer>
)

export { LocationBars }



// WEBPACK FOOTER //
// ./src/Kiosk/LocationsModal/LocationBars.js